/**
 * What's working and what to post next, for social posts.
 *
 * Pure. Built only from what we hold: our own posts (with the topic, format and opening we tag them with), whether
 * Sociamonials delivered them, and the person's earlier decisions. Sociamonials reports delivery, not clicks or likes, so the
 * bars say "delivered" and never claim engagement. A group with too few posts says "Not enough data" and is never ranked.
 */
import { z } from "zod";
import { postFormat, postHook, stageOf, type PostFormat, type PostHook, type StageInput } from "./socialStage.ts";
import type { PastDecision } from "./socialReview.ts";

export const INSIGHT_WINDOW_DAYS = 30;
export const MIN_GROUP_POSTS = 3;

export interface InsightPost extends StageInput {
  text: string;
  imageUrls: readonly string[];
  videoUrl: string | null;
  topic: string | null;
  createdAt: string;
}

export const insightGroupSchema = z.object({
  label: z.string(),
  posts: z.number().int(),
  delivered: z.number().int(),
  /** Share delivered, 0 to 100, for the bar. */
  pct: z.number(),
  note: z.enum(["best", "not_enough"]).nullable(),
});
export type InsightGroup = z.infer<typeof insightGroupSchema>;

export const nextItemSchema = z.object({ kind: z.enum(["more", "test", "skip"]), title: z.string(), reason: z.string() });
export type NextItem = z.infer<typeof nextItemSchema>;

export const socialInsightsSchema = z.object({
  windowDays: z.number().int(),
  /** Posts we tried to send in the window. */
  sent: z.number().int(),
  topic: z.array(insightGroupSchema),
  format: z.array(insightGroupSchema),
  hook: z.array(insightGroupSchema),
  next: z.array(nextItemSchema),
});
export type SocialInsights = z.infer<typeof socialInsightsSchema>;

function group(rows: Array<{ label: string; delivered: boolean }>): InsightGroup[] {
  const by = new Map<string, { posts: number; delivered: number }>();
  for (const r of rows) {
    const g = by.get(r.label) ?? { posts: 0, delivered: 0 };
    g.posts += 1;
    if (r.delivered) g.delivered += 1;
    by.set(r.label, g);
  }
  const list = [...by.entries()].map(([label, g]): InsightGroup => ({ label, posts: g.posts, delivered: g.delivered, pct: Math.round((g.delivered / g.posts) * 100), note: g.posts < MIN_GROUP_POSTS ? "not_enough" : null }));
  list.sort((a, b) => b.posts - a.posts || a.label.localeCompare(b.label));
  // "Best" is only said when at least two groups have enough posts to compare.
  const comparable = list.filter((g) => g.note === null);
  if (comparable.length >= 2) {
    const top = comparable.reduce((a, b) => (b.pct > a.pct || (b.pct === a.pct && b.posts > a.posts) ? b : a));
    const clearWin = comparable.filter((g) => g.pct === top.pct).length === 1;
    if (clearWin) top.note = "best";
  }
  return list;
}

const HOOK_PHRASE: Record<PostHook, string> = { Number: "a number", Question: "a question", Statement: "a statement" };
const HOOKS: PostHook[] = ["Number", "Question", "Statement"];

export function buildSocialInsights(posts: readonly InsightPost[], decisions: readonly PastDecision[], now: Date): SocialInsights {
  const since = now.getTime() - INSIGHT_WINDOW_DAYS * 86_400_000;
  const inWindow = posts.filter((p) => Date.parse(p.createdAt) >= since && stageOf(p) !== "cancelled");
  const sent = inWindow.filter((p) => ["posted", "failed"].includes(stageOf(p)));
  const rows = sent.map((p) => ({ p, delivered: stageOf(p) === "posted" }));

  const topic = group(rows.filter((r) => r.p.topic).map((r) => ({ label: r.p.topic!, delivered: r.delivered })));
  const format = group(rows.map((r) => ({ label: postFormat(r.p), delivered: r.delivered })));
  const hook = group(rows.map((r) => ({ label: postHook(r.p.text), delivered: r.delivered })));

  const next: NextItem[] = [];
  const bestTopic = topic.find((g) => g.note === "best");
  if (bestTopic) next.push({ kind: "more", title: `More posts about ${bestTopic.label}`, reason: `${bestTopic.delivered} of ${bestTopic.posts} were delivered, the best of your topics.` });
  const bestFormat = format.find((g) => g.note === "best");
  if (bestFormat) next.push({ kind: "more", title: `More ${bestFormat.label.toLowerCase()} posts`, reason: `${bestFormat.delivered} of ${bestFormat.posts} were delivered, the best of your formats.` });

  // Test an opening we have not used lately, against the one we use most.
  const used = new Map<PostHook, number>();
  for (const p of inWindow) used.set(postHook(p.text), (used.get(postHook(p.text)) ?? 0) + 1);
  const most = [...used.entries()].sort((a, b) => b[1] - a[1])[0];
  if (most && most[1] >= MIN_GROUP_POSTS) {
    const untested = HOOKS.find((h) => !used.has(h));
    if (untested) next.push({ kind: "test", title: `Opening with ${HOOK_PHRASE[untested]} instead of ${HOOK_PHRASE[most[0]]}`, reason: `We've opened with ${HOOK_PHRASE[most[0]]} ${most[1]} times. ${HOOK_PHRASE[untested][0]!.toUpperCase()}${HOOK_PHRASE[untested].slice(1)} is untested.` });
  }

  // Skip what the person keeps turning down.
  const skip = (label: string, rows2: readonly PastDecision[], title: string) => {
    if (rows2.length < MIN_GROUP_POSTS) return;
    const declined = rows2.filter((r) => !r.approved).length;
    if (declined / rows2.length >= 0.66) next.push({ kind: "skip", title, reason: `You turned down ${declined} of ${rows2.length} ${label}.` });
  };
  for (const h of HOOKS) skip(`posts that open with ${HOOK_PHRASE[h]}`, decisions.filter((d) => d.hook === h), `Posts that open with ${HOOK_PHRASE[h]}`);
  for (const f of ["Text", "Text + image", "Video"] as PostFormat[]) skip(`${f.toLowerCase()} posts`, decisions.filter((d) => d.format === f), `${f} posts`);

  return { windowDays: INSIGHT_WINDOW_DAYS, sent: sent.length, topic, format, hook, next: next.slice(0, 5) };
}
